import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { apiGet } from '../lib/api';
import type { SEOData } from '../components/SEOHead';

interface Question {
  id: string;
  question: string;
  answer: string | BlocksContent;
}

interface Category {
  id: string; // used as slug/anchor if available
  title: string;
  questions: Question[];
}

interface FAQContextValue {
  pageTitle?: string | BlocksContent;
  decorativeTitle?: string;
  everythingElseTitle?: string;
  everythingElse?: string | BlocksContent;
  buttonText?: string;
  buttonUrl?: string;
  seo?: SEOData;
  categories: Category[];
  isLoading: boolean;
  error: unknown;
}

const FAQContext = createContext<FAQContextValue>({ pageTitle: undefined, decorativeTitle: undefined, everythingElseTitle: undefined, everythingElse: undefined, buttonText: undefined, buttonUrl: undefined, seo: undefined, categories: [], isLoading: false, error: null });

// GET /api/faq is a hand-built flat object, not a standard Strapi v4/v5 shape —
// `seo` sits as a top-level sibling key, not nested under data/attributes.
type FAQResponse = { pageTitle?: string | BlocksContent; decorativeTitle?: string; everythingElseTitle?: string; everythingElse?: string | BlocksContent; buttonText?: string; buttonUrl?: string; seo?: SEOData; categories: Category[] };

async function fetchFaq(): Promise<FAQResponse> {
  const base = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337/api';
  const url = `${base}/faq`;
  const res = await apiGet(url);
  if (!res.ok) throw new Error(`Failed to fetch FAQ: ${res.statusText}`);
  const json = await res.json();
  const categoriesData = json.categories || [];
  const categories: Category[] = categoriesData.map((c: any) => ({
    id: String(c.slug || c.id),
    title: c.title || '',
    questions: (c.questions || []).map((q: any) => ({
      id: String(q.id),
      question: q.question || '',
      answer: q.answer ?? '',
    })),
  }));
  return {
    pageTitle: json.pageTitle,
    decorativeTitle: json.decorativeTitle,
    everythingElseTitle: json.everythingElseTitle,
    everythingElse: json.everythingElse,
    buttonText: json.buttonText,
    buttonUrl: json.buttonUrl,
    seo: json.seo,
    categories,
  };
}

export function StrapiFAQProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery<FAQResponse, Error>({
    queryKey: ['faq'],
    queryFn: fetchFaq,
    staleTime: 1000 * 60,
  });

  const categories = data?.categories ?? [];
  const pageTitle = data?.pageTitle;
  const decorativeTitle = data?.decorativeTitle;
  const everythingElseTitle = data?.everythingElseTitle;
  const everythingElse = data?.everythingElse;
  const buttonText = data?.buttonText;
  const buttonUrl = data?.buttonUrl;
  const seo = data?.seo;

  return (
    <FAQContext.Provider value={{ pageTitle, decorativeTitle, everythingElseTitle, everythingElse, buttonText, buttonUrl, seo, categories, isLoading, error }}>
      {children}
    </FAQContext.Provider>
  );
}

export function useFaqs() {
  return useContext(FAQContext);
}
